import { useEffect, useRef, useState } from 'react';

import { useInView } from 'react-intersection-observer';

import './App.css';

import BackTop from './assets/images/back_top.svg';
import Call from './assets/images/call.svg';
import Capability1 from './assets/images/capability1.png';
import Capability2 from './assets/images/capability2.png';
import Capability3 from './assets/images/capability3.png';
import Capability4 from './assets/images/capability4.png';
import Capability5 from './assets/images/capability5.png';
import Capability6 from './assets/images/capability6.png';
import Capability7 from './assets/images/capability7.png';
import Capability8 from './assets/images/capability8.png';
import Capability9 from './assets/images/capability9.png';
import Email from './assets/images/email.svg';
import Feature1 from './assets/images/feature1.svg';
import Feature2 from './assets/images/feature2.svg';
import Feature3 from './assets/images/feature3.svg';
import GalileoLogo from './assets/images/logo.svg';
import Map1 from './assets/images/map1.png';
import Map2 from './assets/images/map2.png';
import Map3 from './assets/images/map3.png';
import Map4 from './assets/images/map4.png';
import Qrcode from './assets/images/qrcode.png';
import { CLICK_REPORT } from './datahub';

const SCROLL_THRESHOLD = 300;
const ANIMATION_THRESHOLD = 0.1;

const features = [
  {
    icon: Feature1,
    title: '端到端全链路可观测',
  },
  {
    icon: Feature2,
    title: '全栈可观测能力',
  },
  {
    icon: Feature3,
    title: '智能根因定位',
  },
];

const productCapabilities = [
  {
    id: 1,
    title: '服务刻画自动生成链路',
    description: '基于Trace、Metric、Event等数据对服务进行刻画，自动生成服务调用链路',
    image: Capability1,
  },
  {
    id: 2,
    title: '后端服务数据可联动一切',
    description: '提供后端的主被调、DB、Runtime等分析能力 ， 可以绑定容器资源等；提供各种数据类型关联跳转和分析',
    image: Capability2,
  },
  {
    id: 3,
    title: '丰富的前端监控',
    description: '小程序、WEB、Hippy 、kuikly、electron、pixui均可开箱即用',
    image: Capability3,
  },
  {
    id: 4,
    title: 'Trace调用链查看细节',
    description: '支持瀑布图、时序图、调用图等，跳转查看session、指标和相关日志',
    image: Capability4,
  },
  {
    id: 5,
    title: 'Log 功能强大又便宜',
    description: '提供完整的Log查询语句、日志转指标、日志告警等能力，并且价格非常的低廉',
    image: Capability5,
  },
  {
    id: 6,
    title: 'Profile功能发现程序性能问题',
    description: '提供火焰图和对比能力，帮助开发者发现CPU、MEM等程序性能瓶颈',
    image: Capability6,
  },
  {
    id: 7,
    title: '集成各种事件让变更无处遁形',
    description: '可以将影响程序稳定性的各种事件进行上报，事件可以与指标图表进行关联展示，也可以进行告警和帮助根因定位',
    image: Capability7,
  },
  {
    id: 8,
    title: '各种场景化告警策略',
    description:
      '支持各种类型数据的告警和各种场景的告警，分场景的告警策略，能针对性的简化配置，提升易用性，有利于持续保持告警的有效性',
    image: Capability8,
  },
  {
    id: 9,
    title: '提供自定义指标和看板',
    description: '除了默认的观测场景化，还提供了更多灵活的自定义指标和看板定制能力',
    image: Capability9,
  },
];

// Popover 组件
const Popover = ({ children, content, placement = 'left' }) => {
  const [visible, setVisible] = useState(false);

  return (
    <div className="popover-container" onMouseEnter={() => setVisible(true)} onMouseLeave={() => setVisible(false)}>
      {children}
      {visible && <div className={`qrcode-popover  popover-content popover-${placement}`}>{content}</div>}
    </div>
  );
};

function App() {
  const [activeCapabilityId, setActiveCapabilityId] = useState(1);
  const [showBackToTop, setShowBackToTop] = useState(false);

  const scrollContainerRef = useRef(null);

  // 控制不同模块进入动画
  const inViewConfig = { triggerOnce: true, threshold: ANIMATION_THRESHOLD };
  const { ref: featureCardsRef, inView: featureCardsInView } = useInView(inViewConfig);
  const { ref: productFeatureRef, inView: productFeatureInView } = useInView(inViewConfig);
  const { ref: productCapabilityRef, inView: productCapabilityInView } = useInView(inViewConfig);
  const { ref: mapRef1, inView: map1InView } = useInView(inViewConfig);
  const { ref: mapRef2, inView: map2InView } = useInView(inViewConfig);
  const { ref: mapRef3, inView: map3InView } = useInView(inViewConfig);
  const { ref: footerRef, inView: footerInView } = useInView(inViewConfig);

  // 监听滚动事件，控制回到顶部按钮显示
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(!!scrollContainerRef.current && scrollContainerRef.current.scrollTop > SCROLL_THRESHOLD);
    };

    const scrollContainer = scrollContainerRef.current;
    scrollContainer?.addEventListener('scroll', handleScroll);
    return () => scrollContainer?.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogin = () => {
    window.location.href = `${window.location.origin}/account/login`;
  };

  const handleBackToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  const handleCardClick = (index) => {
    // 使用 data-section 属性定位元素，而不是索引
    const element = document.querySelector(`[data-section="${index}"]`);
    if (element) {
      const elementTop = element.getBoundingClientRect().top;
      scrollContainerRef.current?.scrollTo({
        top: elementTop,
        behavior: 'smooth',
      });
    }
  };

  const polygonSvg = (
    <svg width="10" height="10" viewBox="0 0 10 10" className="mr12">
      <polygon points="5,0 10,5 5,10 0,5" fill="#fff" />
    </svg>
  );

  return (
    <div className="galiosight-home" ref={scrollContainerRef}>
      <div className="home-wrapper relative flex-col">
        <div className="base-background-img header-bg" />
        <div className="base-background-img footer-bg" />

        <header className="header flex-row space-between">
          <div className="flex-row">
            <img src={GalileoLogo} alt="GalioSight logo" className="logo" />
            <nav className="flex-row menu">
              <a href={`${window.location.origin}/docs/`} target="_blank" rel="noreferrer">
                产品介绍
              </a>
              <a href={`${window.location.origin}/docs/intergrations/overview.html`} target="_blank" rel="noreferrer">
                文档中心
              </a>
              <a href={`${window.location.origin}/docs/guide/terminology.html`} target="_blank" rel="noreferrer">
                使用手册
              </a>
            </nav>
          </div>
          <button className="login-btn pointer" onClick={handleLogin}>
            Login
          </button>
        </header>

        <main className="main flex-col align-center">
          <div className="main-header flex-col align-center">
            <h1 className="title">
              <span className="highlight">GalioSight</span>
              <p>全场景全领域可观测平台</p>
            </h1>
            <p className="subtitle">GalioSight 经济高效一站式可观测，全链路数据贯通，让故障诊断与质量保障更简单</p>
            <button className="main-login-btn pointer" onClick={handleLogin}>
              Login
            </button>
          </div>

          {/* 功能卡片 */}
          <section
            className={`feature-cards flex-row flex-nowrap align-center justify-center${
              featureCardsInView ? ' active' : ''
            }`}
            ref={featureCardsRef}
          >
            {features.map(({ icon, title }, index) => (
              <div
                className={`card flex-col align-center justify-center ${CLICK_REPORT}`}
                key={title}
                onClick={() => handleCardClick(index)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleCardClick(index);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`跳转到${title}部分`}
              >
                <img src={icon} alt={title} className="icon" />
                <h3 className="card-title">{title}</h3>
              </div>
            ))}
          </section>

          {/* 端到端全链路模块 */}
          <section className={`map-section flex-row${map1InView ? ' active' : ''}`} ref={mapRef1} data-section="0">
            <div className="map-image">
              <div className="image-placeholder">
                <img src={Map1} alt="端到端全链路可观测示意图" />
              </div>
            </div>
            <div className="map-desc">
              <img src={Feature1} className="mb16" alt="端到端全链路可观测" />
              <h3>端到端全链路可观测</h3>
              <p>​​让每一次业务旅程清晰可见，让每一处性能瓶颈无所遁形​</p>
              <li className="mb8">
                <span>从用户点击到服务响应，跨越APP前端、微服务、数据库、云原生环境的全链路追踪</span>
              </li>
              <li>
                <span>通过Session、TraceID贯穿复杂系统​​，彻底击穿数据信息孤岛</span>
              </li>
            </div>
          </section>

          <section
            className={`map-section flex-row section-right${map2InView ? ' active' : ''}`}
            ref={mapRef2}
            data-section="1"
          >
            <div className="map-desc">
              <img src={Feature2} className="mb16" alt="全栈可观测能力" />
              <h3>全栈可观测能力</h3>
              <p>平台兼容一切技术栈，五维数据驱动深度洞察​</p>
              <ul>
                <li className="flex-row mb8">
                  {polygonSvg}
                  <span>支持各种技术栈的数据接入和观测能力</span>
                </li>
                <li className="flex-row mb8">
                  {polygonSvg}
                  <span>支持五类数据Metric、Trace、Log、Event、Profile</span>
                </li>
                <li className="flex-row mb8">
                  {polygonSvg}
                  <span>支持原生OpenTelemetry、Prometheus等协议</span>
                </li>
                <li className="flex-row mb8">
                  {polygonSvg}
                  <a
                    href={`${window.location.origin}/docs/intergrations/overview.html`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    更多请查看接入文档
                  </a>
                </li>
              </ul>
            </div>
            <div className="map-image">
              <div className="image-placeholder">
                <img src={Map2} alt="全栈可观测能力示意图" />
              </div>
            </div>
          </section>

          <section className={`map-section flex-row${map3InView ? ' active' : ''}`} ref={mapRef3} data-section="2">
            <div className="map-image">
              <div className="image-placeholder">
                <img src={Map3} alt="智能根因定位" />
              </div>
            </div>
            <div className="map-desc">
              <img src={Feature3} className="mb16" alt="智能根因定位" />
              <h3>智能根因定位</h3>
              <p>AI驱动的故障手术刀，平均定位时间缩短90%​</p>
              <ul>
                <li className="flex-row mb8">
                  {polygonSvg}
                  <span>故障链路精准刻画、五类数据立体化分析，精准定位故障根因</span>
                </li>
                <li className="flex-row mb8">
                  {polygonSvg}
                  <span>多种Agent创建方式，AI Team协同分析，错误分析无处可逃</span>
                </li>
                <li className="flex-row">
                  {polygonSvg}
                  <span>支持日志分析、Trace分析、指标分析、事件分析等多种场景</span>
                </li>
              </ul>
            </div>
          </section>

          {/* 丰富的产品能力模块 */}
          <section
            className={`capability-section flex-col${productCapabilityInView ? ' active' : ''}`}
            ref={productCapabilityRef}
          >
            <h2 className="title">丰富的产品能力</h2>
            <p>​​让复杂归于简单，让不可见变为可控​​</p>

            <div className="capabilities-container flex-row">
              {/* 左侧能力列表 */}
              <div className="capabilities-menu flex-col">
                {productCapabilities.map(capability => (
                  <div
                    key={capability.id}
                    className={`capability-item flex-row flex-norwrap ${CLICK_REPORT}${
                      activeCapabilityId === capability.id ? ' active' : ''
                    }`}
                    onClick={() => setActiveCapabilityId(capability.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setActiveCapabilityId(capability.id);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label={`选择${capability.title}`}
                    aria-pressed={activeCapabilityId === capability.id}
                  >
                    <span className="capability-number mr12">{capability.id.toString().padStart(2, '0')}</span>
                    <span className="capability-title">{capability.title}</span>
                  </div>
                ))}
              </div>

              {/* 右侧内容区域 */}
              <div className="capabilities-content flex-col">
                <p className="content-header">
                  {productCapabilities.find(c => c.id === activeCapabilityId)?.description}
                </p>
                <div className="content-image flex-col justify-center">
                  <div className="image-bg" />
                  <img
                    key={activeCapabilityId}
                    src={productCapabilities.find(c => c.id === activeCapabilityId)?.image}
                    alt={productCapabilities.find(c => c.id === activeCapabilityId)?.title}
                    className="capability-img"
                  />
                </div>
              </div>
            </div>
          </section>

          <section
            className={`features-section flex-col align-center${productFeatureInView ? ' active' : ''}`}
            ref={productFeatureRef}
          >
            <h2 className="title flex-row">
              <p className="highlight">100+</p>
              <span>优秀产品的共同选择</span>
            </h2>
            <img src={Map4} alt="产品能力展示" className="feature-img" />
          </section>
        </main>

        {/* Footer */}
        <footer className={`footer flex-col align-center${footerInView ? ' active' : ''}`} ref={footerRef}>
          <div className="footer-header flex-col align-center">
            <h1 className="title">
              <span className="highlight mr12">GalioSight</span>
              <span>全场景全领域可观测平台</span>
            </h1>
            <p className="subtitle">GalioSight 经济高效一站式可观测，全链路数据贯通，让故障诊断与质量保障更简单</p>
            <button className="main-login-btn pointer" onClick={handleLogin}>
              Login
            </button>
          </div>
          <div className="footer-content flex-row space-between">
            <div className="content-left">
              <div className="links flex-row">
                <img src={GalileoLogo} alt="GalioSight logo" className="logo" />
                <a href={`${window.location.origin}/docs/`} target="_blank" rel="noreferrer">
                  产品介绍
                </a>
                <a href={`${window.location.origin}/docs/agreements/personal-privacy.html`} target="_blank" rel="noreferrer">
                  隐私服务
                </a>
                <a href={`${window.location.origin}/docs/intergrations/overview.html`} target="_blank" rel="noreferrer">
                  文档中心
                </a>
                <a href={`${window.location.origin}/docs/guide/getting-started.html`} target="_blank" rel="noreferrer">
                  使用手册
                </a>
              </div>
              <div className="copyright">
                Copyright © 1998 -2023 Tencent. All Rights Reserved. 腾讯公司版权所有 主体备案号 粤B2-20090059
              </div>
            </div>
            <div className="content-right flex-row justify-center align-center">
              <div>
                <p className="email flex-row mb8">
                  <img src={Email} alt="邮箱" className="mr6" width={26} height={26} />
                  邮箱：
                </p>
                <p>sales@galiosight.cn</p>
              </div>
              <div className="divider divider-vertical" />
              <div className="flex-row justify-center align-center qrcode">
                <p className="mr8">企业微信</p>
                <div className="img-wrapper flex-col">
                  <img src={Qrcode} alt="企业微信二维码" />
                </div>
              </div>
            </div>
          </div>
        </footer>

        {/* 回到顶部按钮 */}
        <div className="fixed-button-group">
          <div className="flex-col align-center jusify-center">
            <Popover
              placement="left"
              content={
                <div className="flex-col justify-center">
                  <p>企业微信</p>
                  <span>微信扫描二维码添加客服，为您提供专属服务</span>
                  <div className="flex-col justify-center align-center">
                    <div className="img-wrapper flex-col">
                      <img src={Qrcode} alt="企业微信二维码" />
                    </div>
                  </div>
                </div>
              }
            >
              <p className="help flex-row justify-center pointer">
                <img src={Call} alt="客服" width={20} height={20} />
              </p>
            </Popover>

            {showBackToTop && (
              <>
                <div className="divider divider-horizontal" />
                <button className="back-to-top" onClick={handleBackToTop}>
                  <img src={BackTop} alt="回到顶部" width={18} height={18} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
